import { createHook, createStore, defaults } from 'react-sweet-state';
import _ from 'lodash';
import moment from 'moment-timezone';
import SessionService from '../services/SessionService';
import {
    ADMIN_ROLES,
    NG1_PRIVILEGE_UNIVERSAL_MONITOR,
    ROLE_NAMES,
    SYSTEM_ADMIN_ROLE,
    USER_SOURCE_LDAP,
    USER_SOURCE_NG1,
    USER_SOURCE_NGP,
    USER_SOURCE_SAML,
} from '../components/pages-admin/users/UserConstants';

// Integrate react-sweet-state with the redux dev tools.
defaults.devtools = true;

// This is not ideal, but it provides access to the time zone outside of React components, which is necessary for the
// DateTimeUtilities to be able to convert to the user's time zone.
let timeZone;
export function getTimeZone() {
    return timeZone || moment.tz.guess();
}

// Get the users time format setting to determine if using AM/PM or 24hr format
// true (use AM/PM) is the default
let meridiemTimeFormat;
export function getMeridiemTimeFormat() {
    return _.isBoolean(meridiemTimeFormat) ? meridiemTimeFormat : true;
}

let refreshTimeout;

/**
 * We believe the JWT is expired, just sync up with the server to make sure
 *  If the server tells us the JWT is expired, log the user out
 * @returns {*}
 */
async function refreshStatus(environment, dispatch) {
    try {
        const token = await SessionService.checkToken();

        if (token?.active) {
            return scheduleRefresh(environment, dispatch);
        }

        return dispatch(clearSession());
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error checking token', e);
    }
}

/**
 * 1) If we believe the session is over, we'll start polling with the server
 *   to make sure it's timed out.  Once it is, we'll kick them out
 * 2) If they are active, then refresh the JWT
 * 3) The session hasn't expired and the user isn't active, schedule a refresh to check back
 * @param {Object} sessionInfo
 * @param {Function} dispatch
 * @returns {void|*}
 */
function refreshJwtForSystemTimeout(environment, dispatch) {
    if (SessionService.isSessionExpired(environment?.systemTimeoutMinutes)) {
        return refreshStatus(environment, dispatch);
    }

    if (SessionService.isUserActive()) {
        return dispatch(refresh(environment));
    }

    return scheduleRefresh(environment, dispatch);
}

/**
 * System timeout hasn't been setup, refresh at a different interval, and no
 * logic to see if we need to refresh, we always will
 */
function refreshJwtForNoSystemTimeout(environment, dispatch) {
    dispatch(refresh(environment));
}

function cancelAutoRefresh() {
    clearTimeout(refreshTimeout);
}

/**
 * We need to keep the JWT up to date, two paths here, if system timeout
 * isn't setup, we just refresh at the interval the API tells us.
 * If system timeout is enabled, we keep the JWT up to date as long
 * as they are active in the system, once it's not active anymore
 * we start polling to try to log them out as their session ends.
 */
function scheduleRefresh(environment, dispatch) {
    cancelAutoRefresh();

    refreshTimeout = setTimeout(
        environment?.systemTimeout
            ? () => refreshJwtForSystemTimeout(environment, dispatch)
            : () => refreshJwtForNoSystemTimeout(environment, dispatch),
        environment?.refreshRateMs || 10000
    );
}

function refresh(environment, shouldRefresh = true) {
    return ({ setState, dispatch }) => {
        return SessionService.refresh()
            .then((sessionInfo) => {
                if (!_.isEmpty(sessionInfo)) {
                    setState({
                        ...sessionInfo,
                        initialized: true,
                        authorized: true,
                    });
                    timeZone = sessionInfo?.userInfo?.user?.timeZone;
                    meridiemTimeFormat = sessionInfo?.userInfo?.user?.meridiemTimeFormat;
                }

                if (shouldRefresh) scheduleRefresh(environment, dispatch);
            })
            .catch((error) => {
                if (error.getStatus && error.getStatus() === 401) {
                    dispatch(clearSession());
                } else {
                    // eslint-disable-next-line no-console
                    console.error('Error refreshing session', error);
                }
            });
    };
}

function clearSession() {
    return ({ setState }) => {
        setState({
            initialized: true,
            authorized: false,
            userInfo: {},
        });
    };
}

function setSession(session) {
    return ({ setState }) => setState(session);
}

function setDarkMode(darkMode) {
    return ({ setState, getState }) =>
        setState({
            ...getState(),
            userInfo: {
                ...getState().userInfo,
                user: {
                    ...getState().userInfo.user,
                    darkMode,
                },
            },
        });
}

function setMeridiemTimeFormat() {
    return ({ setState, getState }) =>
        setState({
            userInfo: {
                ...getState().userInfo,
                user: {
                    ...getState().userInfo.user,
                    meridiemTimeFormat,
                },
            },
        });
}

function setUser({
    firstName,
    lastName,
    email,
    userName,
    timeZone: latestTimeZone,
    meridiemTimeFormat: latestMeridiemTimeFormat,
    darkMode,
}) {
    timeZone = latestTimeZone;
    meridiemTimeFormat = latestMeridiemTimeFormat;
    return ({ setState, getState }) =>
        setState({
            ...getState(),
            userInfo: {
                ...getState().userInfo,
                user: {
                    ...getState().userInfo.user,
                    firstName,
                    lastName,
                    email,
                    userName,
                    timeZone: latestTimeZone,
                    meridiemTimeFormat: latestMeridiemTimeFormat,
                    darkMode,
                },
            },
        });
}

const Store = createStore({
    name: 'Session',

    initialState: {
        initialized: false,
        authorized: false,
        userInfo: {},
    },

    actions: {
        refresh,
        setSession,
        clearSession,
        setDarkMode,
        setMeridiemTimeFormat,
        setUser,
    },
});

export const useIsUserAuthorized = createHook(Store, {
    selector: (state) => state?.authorized,
});

export const useAccessToken = createHook(Store, {
    selector: (state) => state?.userInfo?.accessToken,
});

export const useUser = createHook(Store, {
    selector: (state) => state?.userInfo?.user,
});

export function buildUserDisplayName(user = {}) {
    const firstName = user?.firstName || '';
    const lastName = user?.lastName || '';
    const userName = user?.userName || '';
    const email = user?.email || '';

    if (!_.isEmpty(firstName) && !_.isEmpty(lastName)) {
        return `${firstName} ${lastName}`;
    }

    if (!_.isEmpty(userName)) {
        return userName;
    }

    return email;
}

export const useUserName = createHook(Store, {
    selector: (state) => buildUserDisplayName(state?.userInfo?.user || {}),
});

export const useUserId = createHook(Store, {
    selector: (state) => state?.userInfo?.user?._id,
});

export const useRole = createHook(Store, {
    selector: (state) => state?.userInfo?.user?.userRole,
});

export const useRoleName = createHook(Store, {
    selector: (state) => ROLE_NAMES[state?.userInfo?.user?.userRole],
});

export const useIsAdminUser = createHook(Store, {
    selector: (state) => ADMIN_ROLES.includes(state?.userInfo?.user?.userRole),
});

export const useIsSysAdminUser = createHook(Store, {
    selector: (state) => state?.userInfo?.user?.userRole === SYSTEM_ADMIN_ROLE,
});

export const useIsNgpUser = createHook(Store, {
    selector: (state) =>
        state?.userInfo?.user?.source === USER_SOURCE_NGP || state?.userInfo?.user?.source === 'default',
});

export const useIsNg1User = createHook(Store, {
    selector: (state) => state?.userInfo?.user?.source === USER_SOURCE_NG1,
});

export const useIsLdapUser = createHook(Store, {
    selector: (state) => state?.userInfo?.user?.source === USER_SOURCE_LDAP,
});

export const useIsSamlUser = createHook(Store, {
    selector: (state) => state?.userInfo?.user?.source === USER_SOURCE_SAML,
});

export const useIsNg1UniversalMonitorAllowed = createHook(Store, {
    selector: (state) => state?.userInfo?.user?.nG1Privileges?.includes(NG1_PRIVILEGE_UNIVERSAL_MONITOR),
});

export const useTimezone = createHook(Store, {
    selector: (state) => state?.userInfo?.user?.timeZone || moment.tz.guess(),
});

export const useMeridiemTimeFormat = createHook(Store, {
    selector: (state) =>
        _.isBoolean(state?.userInfo?.user?.meridiemTimeFormat) ? state?.userInfo?.user?.meridiemTimeFormat : true,
});

export const useIsDarkMode = createHook(Store, {
    selector: (state) => state?.userInfo?.user?.darkMode,
});

export const useUserGroups = createHook(Store, {
    selector: (state) => state?.userInfo?.user?.groups,
});

export const useGroupPages = createHook(Store, {
    selector: (state) => state?.userInfo?.groups?.pages,
});

export const useLandingPage = createHook(Store, {
    selector: (state) => state?.userInfo?.groups?.landingPage,
});

export const useAllowDrillIn = createHook(Store, {
    selector: (state) => state?.userInfo?.groups?.allowDrillIn,
});

export const useGroupSites = createHook(Store, {
    selector: (state) => state?.userInfo?.groups?.sites,
});

export const useGroupBusinessServices = createHook(Store, {
    selector: (state) => state?.userInfo?.groups?.businessServices,
});

const useSession = createHook(Store);

export default useSession;
